import { z } from "zod";

/**
 * "Unknown minus `undefined`" — every value a `z.unknown()` schema accepts at
 * runtime except the literal `undefined`. `NonNullable<unknown>` resolves to
 * `unknown & {}` in TypeScript 4.9+, which excludes both `null` and
 * `undefined`; re-unioning with `null` puts `null` back.
 *
 * This is a strict subtype of `unknown`, so `DefinedUnknown extends unknown`
 * holds — schemas that use it still `satisfies z.ZodType<T>` where the
 * authored field type is `unknown`.
 */
type DefinedUnknown = NonNullable<unknown> | null;

/**
 * Workaround for https://github.com/colinhacks/zod/issues/2966.
 *
 * Zod's object type machinery marks a field as optional when the field
 * schema's output type includes `undefined` (`undefined extends T`). Because
 * `unknown` includes `undefined`, a plain `z.unknown()` inside `z.object({...})`
 * is inferred as `{ field?: unknown }` — even though the authored TS type
 * declares the field as required.
 *
 * This helper returns a schema whose runtime behaviour is identical to
 * `z.unknown()` (accepts any value, including `undefined`), but whose
 * `_output` / `_input` types are cast to {@link DefinedUnknown}. Because
 * that type excludes `undefined`, the field renders as required in the
 * inferred shape, and `satisfies z.ZodType<T>` works for fields authored as
 * `unknown`.
 *
 * Use this for every object field whose authored type is required `unknown`.
 */
export function requiredUnknown(): z.ZodType<
  DefinedUnknown,
  z.ZodTypeDef,
  DefinedUnknown
> {
  return z.unknown() as z.ZodType<
    DefinedUnknown,
    z.ZodTypeDef,
    DefinedUnknown
  >;
}
